import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import type {IInvoiceTypeTemplate} from './core/_models'
import {InvoiceTypeList, NOT_ANNOUNCED_LABEL} from './core/_models'
import {SelectInvoiceTypeBox} from './InvoiceTypeSelectBox'
import {useAuth} from '../../../auth'
import './InvoiceTypeSelect.css'

export interface IInvoiceFav {
  type: number
  template: number
}

export type InvoiceTemplateChoice = {
  type: IInvoiceTypeTemplate
  template: IInvoiceTypeTemplate
}

type InvoiceTemplateGroup = {
  id: number
  template: IInvoiceTypeTemplate
  choices: InvoiceTemplateChoice[]
}

type Props = {
  invoiceType: {
    invoiceType: number
    setInvoiceType: Dispatch<SetStateAction<number>>
  }
  invoiceTemplate: {
    invoiceTemplate: number
    setInvoiceTemplate: Dispatch<SetStateAction<number>>
  }
  title: {
    title: string
    setTitle: Dispatch<SetStateAction<string>>
  }
}

const buildTemplateGroups = (): InvoiceTemplateGroup[] => {
  const groups: InvoiceTemplateGroup[] = []

  InvoiceTypeList.filter((type) => type.isActive && (type.id === 1 || type.id === 2)).forEach(
    (type) => {
      type.template
        ?.filter((template) => template.isActive)
        .forEach((template) => {
          const existing = groups.find((group) => group.id === template.id)
          const choice = {type, template}
          if (existing) {
            existing.choices.push(choice)
          } else {
            groups.push({id: template.id, template, choices: [choice]})
          }
        })
    }
  )

  return groups
}

const invoiceTemplateGroups = buildTemplateGroups()

const faNumber = (value: number) =>
  String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)])

/** «الگوی ۹۸ (خرید از بورس کالا) در نوع صورتحساب ۱ و ۲», then any type that is itself not announced. */
const buildNotAnnouncedItems = (): string[] => {
  const patterns = new Map<number, {name: string; types: number[]}>()
  InvoiceTypeList.forEach((type) =>
    type.template
      ?.filter((template) => template.notAnnounced)
      .forEach((template) => {
        const item = patterns.get(template.id) || {name: template.shortName, types: []}
        item.types.push(type.id)
        patterns.set(template.id, item)
      })
  )

  const items = Array.from(patterns.entries()).map(
    ([id, item]) =>
      `الگوی ${faNumber(id)} (${item.name}) در نوع صورتحساب ${item.types.map(faNumber).join(' و ')}`
  )
  InvoiceTypeList.filter((type) => type.notAnnounced).forEach((type) =>
    items.push(`نوع صورتحساب ${faNumber(type.id)}`)
  )
  return items
}

const notAnnouncedItems = buildNotAnnouncedItems()

const InvoiceTypeSelect: FC<Props> = ({title, invoiceType, invoiceTemplate}) => {
  const {currentUser} = useAuth()
  const {setTitle} = title
  const [fav, setFav] = useState<IInvoiceFav[] | undefined>([])

  useEffect(() => {
    setFav(currentUser?.infv || [])
  }, [currentUser?.infv])

  useEffect(() => {
    if (invoiceType.invoiceType === 0) {
      setTitle('نوع صورتحساب را انتخاب کنید')
    }
  }, [invoiceType.invoiceType, setTitle])

  const favorites = fav || []
  const isFavoriteChoice = (choice: InvoiceTemplateChoice) =>
    favorites.some(
      (favorite) => favorite.type === choice.type.id && favorite.template === choice.template.id
    )
  const favoriteGroups = invoiceTemplateGroups.filter((group) =>
    group.choices.some(isFavoriteChoice)
  )
  const otherGroups = invoiceTemplateGroups.filter((group) => !group.choices.some(isFavoriteChoice))
  const getOrderedChoices = (group: InvoiceTemplateGroup) =>
    [...group.choices].sort(
      (first, second) => Number(isFavoriteChoice(second)) - Number(isFavoriteChoice(first))
    )

  const renderTemplateGroup = (group: InvoiceTemplateGroup) => (
    <SelectInvoiceTypeBox
      key={group.id}
      template={group.template}
      choices={getOrderedChoices(group)}
      title={title}
      invoiceType={invoiceType}
      invoiceTemplate={invoiceTemplate}
      fav={{fav: favorites, setFav}}
    />
  )

  return (
    <div className='mp-invoice-type-select'>
      {favoriteGroups.length > 0 && (
        <section className='mp-invoice-priority-section' aria-labelledby='favorite-template-title'>
          <div className='mp-invoice-section-head'>
            <div>
              <span className='mp-invoice-step-badge favorite'>
                <i className='las la-star' aria-hidden='true'></i>
                اولویت بالا
              </span>
              <h4 id='favorite-template-title'>الگوهای ستاره‌دار</h4>
            </div>
            <span className='text-gray-500 fs-8'>{favoriteGroups.length} الگو</span>
          </div>
          <div className='mp-invoice-template-grid mp-invoice-priority-grid'>
            {favoriteGroups.map(renderTemplateGroup)}
          </div>
        </section>
      )}

      {otherGroups.length > 0 && (
        <section aria-labelledby='invoice-template-title'>
          <div className='mp-invoice-section-head'>
            <div>
              <span className='mp-invoice-step-badge'>انتخاب الگو</span>
              <h4 id='invoice-template-title'>
                {favoriteGroups.length > 0 ? 'سایر الگوها' : 'الگوهای قابل صدور'}
              </h4>
            </div>
            <span className='text-gray-500 fs-8'>{otherGroups.length} الگو</span>
          </div>

          <div className='mp-invoice-template-grid'>{otherGroups.map(renderTemplateGroup)}</div>
        </section>
      )}

      <aside className='mp-invoice-third-type-note' aria-label='توضیح صورتحساب نوع سوم'>
        <span className='mp-invoice-third-type-icon' aria-hidden='true'>
          <i className='las la-credit-card'></i>
        </span>
        <span>
          <strong>صورتحساب نوع سوم چرا در این فهرست نیست؟</strong>
          <small>
            نوع سوم الگوی مستقلی برای انتخاب ندارد و مربوط به رسید پرداخت صادرشده از دستگاه کارتخوان
            بانکی یا درگاه پرداختی است که به‌عنوان پایانه فروشگاهی مؤدی ثبت شده باشد.
          </small>
        </span>
        <span className='badge badge-light-info'>بدون انتخاب دستی الگو</span>
      </aside>

      {notAnnouncedItems.length > 0 && (
        <aside
          className='mp-invoice-third-type-note mp-invoice-not-announced-note'
          aria-label='الگوهایی که دستورالعمل رسمی آن‌ها اعلام نشده است'
        >
          <span className='mp-invoice-third-type-icon' aria-hidden='true'>
            <i className='las la-hourglass-half'></i>
          </span>
          <span>
            <strong>چرا این الگوها قابل انتخاب نیستند؟</strong>
            <small>
              {notAnnouncedItems.join('، ')} در سایت مستندات فنی سامانه مودیان آمده‌اند، ولی
              دستورالعمل رسمی آن‌ها هنوز اعلام نشده است. تا اعلام رسمی، صدور صورتحساب با آن‌ها ممکن
              نیست.
            </small>
          </span>
          <span className='badge badge-light-warning'>{NOT_ANNOUNCED_LABEL}</span>
        </aside>
      )}
    </div>
  )
}

export {InvoiceTypeSelect}
